import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/db7ndpqtp9sllopf7i50';

const faqs = [
  { q: '本站的软件可以免费下载吗？', a: '本站提供的软件信息与下载入口均为免费展示，点击下载按钮会显示下载准备提示，属于功能演示，不会触发真实文件传输。' },
  { q: '为什么下载按钮没有真正开始下载？', a: '当前站点为演示版本，所有软件与下载链接均为虚构数据，用于展示页面结构与交互流程，待正式接入文件托管后即可正常下载。' },
  { q: '如何反馈软件信息错误？', a: '你可以通过左侧的留言表单提交问题，选择或填写相关信息并描述具体情况，我们会尽快核对处理。' },
  { q: '本站会收集我的个人信息吗？', a: '留言表单仅收集你主动填写的姓名、邮箱与留言内容，用于回复与问题跟进，不会用于其它用途。' },
  { q: '可以投稿推荐新的播放软件吗？', a: '欢迎通过留言表单或联系邮箱向我们推荐优质影视播放软件，我们会评估后决定是否收录。' },
];

const contactChannels = [
  { icon: 'ri-mail-line', label: '联系邮箱', value: 'support@kanpian-soft.com' },
  { icon: 'ri-customer-service-2-line', label: '在线支持', value: '工作日 9:00 - 18:00' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市朝阳区影视产业园 A 座' },
];

const friendLinks = [
  { name: '影视工具导航', href: '#' },
  { name: '播放器评测站', href: '#' },
  { name: '高清素材社区', href: '#' },
  { name: '数字影音协会', href: '#' },
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageMeta(
    '联系方式与友情链接 - 看片软件',
    '通过邮箱或站内留言表单联系「看片软件」团队，获取反馈与支持，并查看常见问题解答与友情链接。',
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('留言已提交，感谢你的反馈！');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('留言已提交，感谢你的反馈！我们会尽快回复你。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(
          typeof serverMsg === 'string' && serverMsg
            ? `提交失败：${serverMsg}`
            : '提交失败，请检查填写内容后重试。',
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <div className="mb-8">
        <Breadcrumb items={[{ label: '联系方式' }]} />
      </div>

      <header className="mb-8">
        <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">联系方式与友情链接</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-500">
          无论是使用问题、软件推荐还是合作洽谈，都欢迎通过以下方式与我们取得联系。我们会认真对待每一条反馈。
        </p>
      </header>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-contacts-book-2-line text-primary-600"></i>联系渠道
            </h2>
            <ul className="flex flex-col gap-4">
              {contactChannels.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-400">{c.label}</span>
                    <span className="block text-sm font-medium text-foreground-900">{c.value}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-5 border-t border-background-200 pt-5">
              <p className="mb-3 text-xs text-foreground-400">社交媒体</p>
              <div className="flex items-center gap-3">
                <a href="#" aria-label="微博" className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-foreground-950">
                  <i className="ri-weibo-fill text-lg"></i>
                </a>
                <a href="#" aria-label="微信" className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-foreground-950">
                  <i className="ri-wechat-fill text-lg"></i>
                </a>
                <a href="#" aria-label="哔哩哔哩" className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-foreground-950">
                  <i className="ri-bilibili-fill text-lg"></i>
                </a>
                <a href="#" aria-label="抖音" className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-foreground-950">
                  <i className="ri-tiktok-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-links-line text-primary-600"></i>友情链接
            </h2>
            <div className="flex flex-wrap gap-2">
              {friendLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  rel="nofollow"
                  className="whitespace-nowrap rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-8">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-2 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-chat-3-line text-primary-600"></i>站内留言
            </h2>
            <p className="mb-5 text-sm text-foreground-500">填写下方表单向我们留言，带 * 的为必填项（演示表单，可正常提交）。</p>

            <form id="contact-message-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="text"
                name="website_alt"
                className="form-verify-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">姓名 <span className="text-accent-600">*</span></span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入你的称呼"
                    className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">邮箱 <span className="text-accent-600">*</span></span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收回复，如 name@example.com"
                    className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-700">留言内容 <span className="text-accent-600">*</span></span>
                  <span className="text-xs text-foreground-400">最多 500 字</span>
                </span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你遇到的问题或想推荐的内容…"
                  className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                ></textarea>
              </label>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line kp-spinner"></i>提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>提交留言
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-400">我们不会向第三方泄露你的联系信息。</p>
              </div>

              {status !== 'idle' && (
                <div
                  className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                    status === 'success'
                      ? 'border-accent-200 bg-accent-50 text-accent-800'
                      : 'border-foreground-300 bg-foreground-100 text-foreground-900'
                  }`}
                >
                  <i className={`${status === 'success' ? 'ri-checkbox-circle-line text-accent-600' : 'ri-error-warning-line text-foreground-700'} mt-0.5`}></i>
                  <span>{message}</span>
                </div>
              )}
            </form>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-question-answer-line text-primary-600"></i>常见问题
            </h2>
            <div className="flex flex-col divide-y divide-background-200">
              {faqs.map((faq, i) => (
                <div key={faq.q} className="py-1">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full cursor-pointer items-center justify-between gap-3 py-3 text-left"
                  >
                    <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} shrink-0 text-lg text-primary-600`}></i>
                  </button>
                  {openFaq === i && (
                    <p className="pb-3 text-sm leading-relaxed text-foreground-500">{faq.a}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-foreground-500">
              仍有疑问？也可以<Link to="/about" className="text-primary-600 hover:text-primary-700">了解关于我们</Link>，或直接发送邮件至 support@kanpian-soft.com。
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}